import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  CheckCircle, XCircle, Clock, AlertTriangle, Trash2, User, Calendar, 
  Filter, ChevronDown, X, Eye, FileText, Package, ShoppingBag,
  ArrowUpDown, Search, Building2, Shield, DollarSign, TrendingDown,
  BarChart3, Activity, Ban, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Status tabs configuration
const STATUS_TABS = [
  { id: 'PENDING', label: 'Pending', color: 'orange', icon: Clock },
  { id: 'APPROVED', label: 'Approved', color: 'blue', icon: CheckCircle },
  { id: 'REJECTED', label: 'Rejected', color: 'red', icon: XCircle },
  { id: 'EXECUTED', label: 'Executed', color: 'green', icon: CheckCircle },
];

// Vendor resource type icons
const RESOURCE_ICONS = {
  vendor: Package,
  bill: FileText,
  item: ShoppingBag,
};

// Accounts entity type icons
const ACCOUNTS_ENTITY_ICONS = {
  SALES_UPLOAD: DollarSign,
  MANUAL_ADJUSTMENT: BarChart3,
  EXPENSE_EXCLUSION: Ban,
};

// Expense Capture source tab RETIRED — Supplier Bill VOID is owned by 1OS Bills.
// Only Accounts Engine deletion/exclusion requests are reviewed in Control Room.

const DeletionApprovals = () => {
  const { user } = useAuth();
  const activeSource = 'accounts';  // Accounts Engine is the only remaining source

  // --- Shared state ---
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [statusCounts, setStatusCounts] = useState({
    PENDING: 0, APPROVED: 0, REJECTED: 0, EXECUTED: 0
  });

  // Super Admin org + outlet context
  const [organizations, setOrganizations] = useState([]);
  const [orgOutlets, setOrgOutlets] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    return localStorage.getItem('super_admin_org_context') || '';
  });
  const [selectedOutletId, setSelectedOutletId] = useState(() => {
    return localStorage.getItem('super_admin_outlet_context') || '';
  });
  const isSuperAdmin = user?.is_super_admin === true;

  // Request data
  const [deleteRequests, setDeleteRequests] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    resource_type: '',
    entity_type: '',
    outlet_id: '',
    search: '',
  });

  // Detail drawer
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  // Rejection modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // User + outlet caches
  const userCacheRef = useRef(new Map());
  const [userMap, setUserMap] = useState({});
  const outletCacheRef = useRef(new Map());
  const [outletMap, setOutletMap] = useState({});

  // --- Context helpers ---
  const getOrgContext = useCallback(() => isSuperAdmin ? selectedOrgId : null, [isSuperAdmin, selectedOrgId]);
  const getOutletContext = useCallback(() => isSuperAdmin ? selectedOutletId : null, [isSuperAdmin, selectedOutletId]);

  // --- Organization loading ---
  useEffect(() => {
    if (isSuperAdmin) {
      api.getOrganizations().then(r => setOrganizations(r.data || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && selectedOrgId) {
      api.getOutlets({ org_id: selectedOrgId }).then(r => setOrgOutlets(r.data || [])).catch(() => setOrgOutlets([]));
    }
  }, [isSuperAdmin, selectedOrgId]);

  useEffect(() => {
    if (selectedOrgId) localStorage.setItem('super_admin_org_context', selectedOrgId);
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedOutletId) localStorage.setItem('super_admin_outlet_context', selectedOutletId);
  }, [selectedOutletId]);

  useEffect(() => {
    if (isSuperAdmin) {
      const belongs = orgOutlets.some(o => o.outlet_id === selectedOutletId);
      if (!belongs) {
        setSelectedOutletId('');
        localStorage.removeItem('super_admin_outlet_context');
      }
    }
  }, [selectedOrgId, orgOutlets, isSuperAdmin, selectedOutletId]);

  // --- User / outlet cache loading ---
  useEffect(() => {
    if (userCacheRef.current.size === 0) {
      api.getUsers().then(r => {
        const map = {};
        (r.data || []).forEach(u => {
          const info = { name: u.full_name || u.name || u.email?.split('@')[0] || 'Unknown', email: u.email };
          map[u.user_id] = info;
          userCacheRef.current.set(u.user_id, info);
        });
        setUserMap(map);
      }).catch(() => {});
    }
    if (outletCacheRef.current.size === 0) {
      api.getOutlets().then(r => {
        const map = {};
        (r.data || []).forEach(o => {
          const info = { name: o.outlet_name || o.name || 'Unknown Outlet', org_name: o.org_name };
          map[o.outlet_id] = info;
          outletCacheRef.current.set(o.outlet_id, info);
        });
        setOutletMap(map);
      }).catch(() => {});
    }
  }, []);

  const getUserInfo = useCallback((userId) => {
    if (!userId) return { name: 'Unknown', email: null };
    return userMap[userId] || userCacheRef.current.get(userId) || { name: userId.slice(0, 8) + '...', email: null };
  }, [userMap]);

  const getOutletInfo = useCallback((outletId) => {
    if (!outletId) return { name: 'Unknown', org_name: null };
    return outletMap[outletId] || outletCacheRef.current.get(outletId) || { name: outletId.slice(0, 8) + '...', org_name: null };
  }, [outletMap]);

  // --- Data loading ---
  const loadRequests = useCallback(async (status) => {
    setLoading(true);
    try {
      const orgCtx = getOrgContext();
      const outletCtx = getOutletContext();
      const response = await api.getAccountsDeleteRequests(status, orgCtx, outletCtx);
      const requests = response.data || [];
      requests.sort((a, b) => new Date(a.requested_at) - new Date(b.requested_at));
      setDeleteRequests(requests);
      setStatusCounts(prev => ({ ...prev, [status]: requests.length }));
    } catch (error) {
      console.error('Error loading requests:', error);
      setDeleteRequests([]);
    } finally {
      setLoading(false);
    }
  }, [activeSource, getOrgContext, getOutletContext]);

  const loadAllStatusCounts = useCallback(async () => {
    try {
      const orgCtx = getOrgContext();
      const outletCtx = getOutletContext();
      const fetcher = api.getAccountsDeleteRequests;
      const results = await Promise.allSettled([
        fetcher('PENDING', orgCtx, outletCtx),
        fetcher('APPROVED', orgCtx, outletCtx),
        fetcher('REJECTED', orgCtx, outletCtx),
        fetcher('EXECUTED', orgCtx, outletCtx),
      ]);
      const counts = { PENDING: 0, APPROVED: 0, REJECTED: 0, EXECUTED: 0 };
      results.forEach((result, i) => {
        const st = ['PENDING', 'APPROVED', 'REJECTED', 'EXECUTED'][i];
        if (result.status === 'fulfilled') counts[st] = (result.value.data || []).length;
      });
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  }, [activeSource, getOrgContext, getOutletContext]);

  // Reload on tab / source / context changes
  useEffect(() => {
    if (isSuperAdmin && (!selectedOrgId || !selectedOutletId)) {
      setDeleteRequests([]);
      setLoading(false);
      return;
    }
    loadRequests(activeTab);
  }, [activeTab, selectedOrgId, selectedOutletId, isSuperAdmin, activeSource, loadRequests]);

  useEffect(() => {
    if (isSuperAdmin && (!selectedOrgId || !selectedOutletId)) {
      setStatusCounts({ PENDING: 0, APPROVED: 0, REJECTED: 0, EXECUTED: 0 });
      return;
    }
    loadAllStatusCounts();
  }, [selectedOrgId, selectedOutletId, isSuperAdmin, activeSource, loadAllStatusCounts]);

  // Reset filters when switching source
  useEffect(() => {
    setFilters({ resource_type: '', entity_type: '', outlet_id: '', search: '' });
    setActiveTab('PENDING');
  }, [activeSource]);

  // --- Filtered list ---
  const filteredRequests = useMemo(() => {
    let filtered = [...deleteRequests];
    if (filters.entity_type) filtered = filtered.filter(r => r.entity_type === filters.entity_type);
    if (filters.outlet_id) filtered = filtered.filter(r => r.outlet_id === filters.outlet_id);
    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(r =>
        (r.vendor_name || '').toLowerCase().includes(s) ||
        (r.resource_id || '').toLowerCase().includes(s) ||
        (r.entity_id || '').toLowerCase().includes(s) ||
        (r.reason || '').toLowerCase().includes(s) ||
        (r.entity_type_label || '').toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [deleteRequests, filters, activeSource]);

  const uniqueOutlets = useMemo(() => {
    const set = new Set();
    deleteRequests.forEach(r => { if (r.outlet_id) set.add(r.outlet_id); });
    return Array.from(set);
  }, [deleteRequests]);

  // --- Actions ---
  const handleApprove = async (requestId) => {
    const confirmMsg = 'This approval will be sent to the Accounts Engine.\nThe Accounts Engine owns execution and will recompute:\nP&L → Forecast → Alerts → Pulse\n\nAre you sure you want to APPROVE?';
    if (!window.confirm(confirmMsg)) return;

    setProcessing(requestId);
    try {
      const orgCtx = getOrgContext();
      const outletCtx = getOutletContext();
      // Control Room is approver-only. The owning engine executes on its side.
      await api.approveAccountsDeleteRequest(requestId, orgCtx, outletCtx);
      await loadRequests(activeTab);
      await loadAllStatusCounts();
      setShowDetailDrawer(false);
      setSelectedRequest(null);
      alert('Approval sent to Accounts Engine. The owning engine will execute and recompute downstream metrics.');
    } catch (error) {
      console.error('Error approving:', error);
      alert(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectModal = (request) => {
    setRejectingRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { alert('Rejection reason is required'); return; }
    const requestId = rejectingRequest.id || rejectingRequest.request_id;
    setProcessing(requestId);
    try {
      const orgCtx = getOrgContext();
      const outletCtx = getOutletContext();
      if (activeSource === 'accounts') {
        await api.rejectAccountsDeleteRequest(requestId, rejectionReason, orgCtx, outletCtx);
      }
      await loadRequests(activeTab);
      await loadAllStatusCounts();
      setShowRejectModal(false);
      setRejectingRequest(null);
      setShowDetailDrawer(false);
      setSelectedRequest(null);
      alert('Request rejected successfully');
    } catch (error) {
      console.error('Error rejecting:', error);
      alert(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this pending request?')) return;
    setProcessing(requestId);
    try {
      const orgCtx = getOrgContext();
      const outletCtx = getOutletContext();
      await api.cancelAccountsDeleteRequest(requestId, orgCtx, outletCtx);
      await loadRequests(activeTab);
      await loadAllStatusCounts();
      setShowDetailDrawer(false);
      setSelectedRequest(null);
      alert('Request cancelled');
    } catch (error) {
      console.error('Error cancelling:', error);
      alert(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setProcessing(null);
    }
  };

  // --- Formatting helpers ---
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getAge = (dateStr) => {
    if (!dateStr) return '-';
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const d = Math.floor(diffMs / 86400000);
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor(diffMs / 60000);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    if (m > 0) return `${m}m ago`;
    return 'Just now';
  };

  const getStatusBadge = (status) => {
    const c = {
      PENDING: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      APPROVED: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      EXECUTED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
    return `${c.bg} ${c.text} border ${c.border}`;
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    const num = Number(val);
    const sign = num >= 0 ? '' : '-';
    return `${sign}₹${Math.abs(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // --- Access check ---
  if (!user?.is_org_admin && !isSuperAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">Only organization administrators or super admins can access deletion approvals.</p>
        </div>
      </div>
    );
  }

  // --- Super Admin context selector ---
  if (isSuperAdmin && (!selectedOrgId || !selectedOutletId)) {
    return (
      <div className="p-8" data-testid="deletion-approvals-page">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Deletion Approvals</h1>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
              <Shield size={12} /> Super Admin
            </span>
          </div>
          <p className="text-gray-600 mt-1">Review and approve deletion requests</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center max-w-lg mx-auto">
          <Building2 className="mx-auto text-amber-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-amber-900 mb-2">Select Context</h2>
          <p className="text-amber-700 mb-6">As a Super Admin, you must select both an organization and outlet.</p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Organization</label>
            <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full px-4 py-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-gray-900"
              data-testid="org-selector">
              <option value="">-- Select Organization --</option>
              {organizations.map(org => <option key={org.org_id} value={org.org_id}>{org.org_name}</option>)}
            </select>
          </div>
          {selectedOrgId && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Outlet</label>
              <select value={selectedOutletId} onChange={(e) => setSelectedOutletId(e.target.value)}
                className="w-full px-4 py-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-gray-900"
                data-testid="outlet-selector">
                <option value="">-- Select Outlet --</option>
                {orgOutlets.map(o => <option key={o.outlet_id} value={o.outlet_id}>{o.outlet_name || o.name}</option>)}
              </select>
              {orgOutlets.length === 0 && <p className="text-sm text-amber-600 mt-2">No outlets found</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Impact preview component for Accounts ---
  const ImpactPreview = ({ impact }) => {
    if (!impact) return null;
    const items = [
      { label: 'Revenue Change', value: impact.revenue_change, icon: DollarSign },
      { label: 'Expense Change', value: impact.expense_change, icon: TrendingDown },
      { label: 'Gross Profit Change', value: impact.gross_profit_change, icon: BarChart3 },
      { label: 'Margin Change', value: impact.margin_change, suffix: impact.margin_change != null ? '%' : '', icon: Activity },
      { label: 'Cash Impact', value: impact.cash_impact, icon: DollarSign },
    ].filter(it => it.value !== null && it.value !== undefined);

    if (items.length === 0) return null;

    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="impact-preview">
        <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <AlertTriangle size={14} />
          Financial Impact Preview
        </h4>
        <div className="grid grid-cols-2 gap-3">
          {items.map(({ label, value, suffix, icon: Icon }) => (
            <div key={label} className="flex items-center gap-2">
              <Icon size={14} className="text-amber-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-amber-700">{label}</p>
                <p className={`text-sm font-semibold ${Number(value) < 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {suffix ? `${Number(value).toFixed(2)}${suffix}` : formatCurrency(value)}
                </p>
              </div>
            </div>
          ))}
        </div>
        {impact.forecast_status_before && (
          <div className="mt-3 pt-3 border-t border-amber-200 text-xs">
            <span className="text-amber-700">Forecast: </span>
            <span className="font-medium text-amber-900">{impact.forecast_status_before}</span>
            <span className="text-amber-700"> → </span>
            <span className="font-medium text-amber-900">{impact.forecast_status_after || 'will recompute'}</span>
          </div>
        )}
        {impact.locked_period_warning && (
          <div className="mt-2 text-xs text-red-700 font-medium">
            Locked period affected: {impact.locked_period_warning}
          </div>
        )}
      </div>
    );
  };

  // --- Get display icon for a request ---
  const getRequestIcon = (request) => {
    if (activeSource === 'accounts') {
      const Icon = ACCOUNTS_ENTITY_ICONS[request.entity_type] || DollarSign;
      return <Icon size={16} />;
    }
    const Icon = RESOURCE_ICONS[request.resource_type?.toLowerCase()] || Package;
    return <Icon size={16} />;
  };

  // --- Get display name for a request ---
  const getRequestName = (request) => {
    if (activeSource === 'accounts') {
      return request.entity_type_label || request.entity_type || 'Accounts Request';
    }
    return request.vendor_name || request.resource_name || request.resource_type || 'Resource';
  };

  const getRequestIdDisplay = (request) => {
    const id = request.entity_id || request.resource_id || request.id || request.request_id || '';
    return id.length > 12 ? id.slice(0, 12) + '...' : id;
  };

  const getIconBg = (request) => {
    if (activeSource === 'accounts') {
      const map = { SALES_UPLOAD: 'bg-emerald-100', MANUAL_ADJUSTMENT: 'bg-indigo-100', EXPENSE_EXCLUSION: 'bg-orange-100' };
      return map[request.entity_type] || 'bg-gray-100';
    }
    const map = { bill: 'bg-blue-100', item: 'bg-purple-100' };
    return map[request.resource_type] || 'bg-red-100';
  };

  // --- Main render ---
  return (
    <div className="p-8" data-testid="deletion-approvals-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Deletion Approvals</h1>
              {isSuperAdmin && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Shield size={12} /> Super Admin
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">
              Review and approve deletion requests from Accounts Engine
            </p>
          </div>
          {isSuperAdmin && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-gray-500" />
                <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  data-testid="org-selector-header">
                  {organizations.map(org => <option key={org.org_id} value={org.org_id}>{org.org_name}</option>)}
                </select>
              </div>
              <select value={selectedOutletId} onChange={(e) => setSelectedOutletId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                data-testid="outlet-selector-header">
                {orgOutlets.map(o => <option key={o.outlet_id} value={o.outlet_id}>{o.outlet_name || o.name}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Legacy governance notice — static, non-interactive. Supplier Bill VOID is owned by 1OS Bills. */}
      <div
        className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        data-testid="supplier-bill-void-retired-notice"
      >
        <Ban size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
        <p className="text-sm text-amber-900">
          <span className="font-semibold">Supplier Bill deletion/void governance has moved to 1OS Bills.</span>{' '}
          Control Room is no longer an approval authority for Supplier Bill void — requests, maker/checker,
          approval and rejection all happen in Bills. Only Accounts Engine deletion/exclusion requests are
          reviewed here. Historical Control Room records are retained read-only for audit.
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <motion.button key={tab.id} onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className={`p-4 rounded-lg border-2 transition-all ${
                isActive ? `border-${tab.color}-500 bg-${tab.color}-50` : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              data-testid={`status-tab-${tab.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className={`text-xs font-medium ${isActive ? `text-${tab.color}-600` : 'text-gray-500'}`}>{tab.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${isActive ? `text-${tab.color}-700` : 'text-gray-900'}`}>{statusCounts[tab.id]}</p>
                </div>
                <div className={`p-2 rounded-lg ${isActive ? `bg-${tab.color}-100` : 'bg-gray-100'}`}>
                  <Icon className={isActive ? `text-${tab.color}-600` : 'text-gray-400'} size={20} />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder={activeSource === 'accounts' ? "Search by entity, ID, or reason..." : "Search by vendor, ID, or reason..."}
              value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="search-filter" />
          </div>
          <select value={filters.entity_type} onChange={(e) => setFilters(f => ({ ...f, entity_type: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg" data-testid="type-filter">
            <option value="">All Entity Types</option>
            <option value="SALES_UPLOAD">Sales Upload</option>
            <option value="MANUAL_ADJUSTMENT">Manual Adjustment</option>
            <option value="EXPENSE_EXCLUSION">Expense Exclusion</option>
          </select>
          {uniqueOutlets.length > 0 && (
            <select value={filters.outlet_id} onChange={(e) => setFilters(f => ({ ...f, outlet_id: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg" data-testid="outlet-filter">
              <option value="">All Outlets</option>
              {uniqueOutlets.map(oid => (
                <option key={oid} value={oid}>{getOutletInfo(oid).name}</option>
              ))}
            </select>
          )}
          {(filters.resource_type || filters.entity_type || filters.outlet_id || filters.search) && (
            <button onClick={() => setFilters({ resource_type: '', entity_type: '', outlet_id: '', search: '' })}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
              <X size={16} /> Clear
            </button>
          )}
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <ArrowUpDown size={14} /><span>Oldest first</span>
          </div>
        </div>
      </div>

      {/* Request List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          {activeTab === 'PENDING' ? (
            <>
              <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
              <p className="text-gray-600 text-lg">No pending {activeSource === 'accounts' ? 'accounts ' : ''}deletion requests</p>
              <p className="text-gray-500 text-sm mt-2">All caught up! Check other tabs for history.</p>
            </>
          ) : (
            <>
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">No {activeTab.toLowerCase()} requests</p>
              <p className="text-gray-500 text-sm mt-2">
                {(filters.resource_type || filters.entity_type || filters.outlet_id) ? 'Try adjusting your filters.' : 'Nothing to show here yet.'}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">{activeSource === 'accounts' ? 'Entity' : 'Resource'}</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Requested By</div>
            <div className="col-span-2">Reason</div>
            <div className="col-span-1">Age</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredRequests.map((request) => {
              const requestId = request.id || request.request_id;
              return (
                <motion.div key={requestId} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  data-testid={`delete-request-${requestId}`}>
                  {/* Name + ID */}
                  <div className="col-span-3 flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getIconBg(request)}`}>{getRequestIcon(request)}</div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{getRequestName(request)}</p>
                      <p className="text-xs text-gray-500 font-mono truncate">{getRequestIdDisplay(request)}</p>
                    </div>
                  </div>
                  {/* Type Badge */}
                  <div className="col-span-2 flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(request.status)}`}>
                      {activeSource === 'accounts' ? (request.entity_type_label || request.entity_type) : (request.resource_type || 'vendor')}
                    </span>
                  </div>
                  {/* Requester */}
                  <div className="col-span-2 flex items-center text-sm">
                    <User size={14} className="mr-1 flex-shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <span className="text-gray-900 truncate block">
                        {request.requested_by_name || getUserInfo(request.requested_by).name}
                      </span>
                    </div>
                  </div>
                  {/* Reason */}
                  <div className="col-span-2 flex items-center text-sm text-gray-600 truncate">
                    {(request.reason || 'No reason').slice(0, 30)}{(request.reason?.length > 30) ? '...' : ''}
                  </div>
                  {/* Age */}
                  <div className="col-span-1 flex items-center text-sm text-gray-500">{getAge(request.requested_at)}</div>
                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <button onClick={() => { setSelectedRequest(request); setShowDetailDrawer(true); }}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details" data-testid={`view-detail-${requestId}`}>
                      <Eye size={18} />
                    </button>
                    {activeTab === 'PENDING' && (
                      <>
                        <button onClick={() => handleApprove(requestId)}
                          disabled={processing === requestId}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`approve-btn-${requestId}`}>
                          {processing === requestId
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            : <><CheckCircle size={14} /> Approve</>}
                        </button>
                        <button onClick={() => openRejectModal(request)}
                          disabled={processing === requestId}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                          data-testid={`reject-btn-${requestId}`}>
                          <XCircle size={14} /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <AnimatePresence>
        {showDetailDrawer && selectedRequest && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowDetailDrawer(false)}
              className="fixed inset-0 bg-black bg-opacity-30 z-40" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Request Details</h2>
                  <button onClick={() => setShowDetailDrawer(false)} className="p-2 hover:bg-gray-100 rounded-lg"
                    data-testid="close-drawer">
                    <X size={20} />
                  </button>
                </div>

                {/* Status */}
                <div className="mb-6">
                  <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${getStatusBadge(selectedRequest.status)}`}>
                    {selectedRequest.status}
                  </span>
                </div>

                {/* Resource / Entity Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">
                    {activeSource === 'accounts' ? 'Entity Information' : 'Resource Information'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium text-gray-900">
                        {activeSource === 'accounts'
                          ? (selectedRequest.entity_type_label || selectedRequest.entity_type)
                          : (selectedRequest.resource_type || 'vendor')}
                      </span>
                    </div>
                    {activeSource === 'accounts' && selectedRequest.action_type && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Action:</span>
                        <span className="font-medium text-gray-900">{selectedRequest.action_type}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">{activeSource === 'accounts' ? 'Entity ID:' : 'Resource ID:'}</span>
                      <span className="font-mono text-xs text-gray-900">
                        {selectedRequest.entity_id || selectedRequest.resource_id || selectedRequest.id}
                      </span>
                    </div>
                    {selectedRequest.outlet_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Outlet:</span>
                        <span className="text-gray-900">
                          {selectedRequest.outlet_name || getOutletInfo(selectedRequest.outlet_id).name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Impact preview — Accounts only */}
                {activeSource === 'accounts' && selectedRequest.impact_snapshot && (
                  <ImpactPreview impact={selectedRequest.impact_snapshot} />
                )}

                {/* Recompute plan — Accounts only */}
                {activeSource === 'accounts' && selectedRequest.recompute_plan && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="recompute-plan">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <RefreshCw size={14} /> Recompute Plan
                    </h4>
                    <ul className="text-xs text-blue-800 space-y-1">
                      {(Array.isArray(selectedRequest.recompute_plan)
                        ? selectedRequest.recompute_plan
                        : [selectedRequest.recompute_plan]
                      ).map((step, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <span className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                          {typeof step === 'string' ? step : JSON.stringify(step)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Request details */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Requested By</label>
                    <div className="mt-1">
                      <p className="text-gray-900 font-medium">
                        {selectedRequest.requested_by_name || getUserInfo(selectedRequest.requested_by).name}
                      </p>
                      {(selectedRequest.requested_by_email || getUserInfo(selectedRequest.requested_by).email) && (
                        <p className="text-sm text-gray-500">
                          {selectedRequest.requested_by_email || getUserInfo(selectedRequest.requested_by).email}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Requested At</label>
                    <p className="mt-1 text-gray-900">{formatDate(selectedRequest.requested_at)}</p>
                    <p className="text-sm text-gray-500">{getAge(selectedRequest.requested_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Reason</label>
                    <p className="mt-1 text-gray-900 bg-gray-50 rounded-lg p-3">
                      {selectedRequest.reason || 'No reason provided'}
                    </p>
                  </div>
                  {selectedRequest.status === 'REJECTED' && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Rejection Reason</label>
                      <p className="mt-1 text-red-700 bg-red-50 rounded-lg p-3">
                        {selectedRequest.rejection_reason || 'No reason provided'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Warning — Accounts vs Vendor */}
                {selectedRequest.status === 'PENDING' && (
                  <div className={`rounded-lg p-4 mb-6 border ${
                    activeSource === 'accounts'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    {activeSource === 'accounts' ? (
                      <p className="text-sm text-red-800" data-testid="accounts-warning">
                        <strong>Approval Notice:</strong> Approving sends the decision to the Accounts Engine.
                        The Accounts Engine will execute the deletion/exclusion and recompute:
                        <strong> P&L → Forecast → Alerts → Pulse</strong>.
                        Control Room does not execute the change directly.
                      </p>
                    ) : (
                      <p className="text-sm text-yellow-800">
                        <strong>Approval Notice:</strong> Approving sends the decision to Expense Capture.
                        The owning engine will execute the deletion and propagate canonical events.
                        Control Room does not execute the deletion directly.
                      </p>
                    )}
                  </div>
                )}

                {/* Actions */}
                {selectedRequest.status === 'PENDING' && (
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(selectedRequest.id || selectedRequest.request_id)}
                      disabled={processing}
                      className="flex-1 px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="drawer-approve-btn">
                      {processing
                        ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        : <><CheckCircle size={18} /> Approve</>}
                    </button>
                    <button onClick={() => openRejectModal(selectedRequest)}
                      disabled={processing}
                      className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="drawer-reject-btn">
                      <XCircle size={18} /> Reject
                    </button>
                    {activeSource === 'accounts' && (
                      <button onClick={() => handleCancel(selectedRequest.id || selectedRequest.request_id)}
                        disabled={processing}
                        className="px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="drawer-cancel-btn">
                        <Ban size={18} /> Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Rejection Modal */}
      <AnimatePresence>
        {showRejectModal && rejectingRequest && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowRejectModal(false)}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg"><XCircle className="text-red-600" size={24} /></div>
                <h3 className="text-xl font-bold text-gray-900">Reject Request</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejecting this {activeSource === 'accounts' ? 'accounts deletion/exclusion' : 'deletion'} request.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..." rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
                  autoFocus data-testid="rejection-reason-input" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleReject}
                  disabled={!rejectionReason.trim() || processing}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-reject-btn">
                  {processing
                    ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    : 'Reject Request'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeletionApprovals;
